import apiClient from '../client';
import type {
  FarmOperation,
  CreateFarmOperationRequest,
  OperationTask,
  CreateOperationTaskRequest,
  HarvestRecord,
  CreateHarvestRecordRequest,
  PaginatedResponse,
  OperationStatus,
} from '../../types';

const farmOperationService = {
  getAll: async (page = 0, size = 20) => {
    const res = await apiClient.get<PaginatedResponse<FarmOperation>>(
      `/farm-operations?page=${page}&size=${size}&sort=createdAt,desc`
    );
    return res.data;
  },

  getById: async (id: string) => {
    const res = await apiClient.get<FarmOperation>(`/farm-operations/${id}`);
    return res.data;
  },

  getByProject: async (projectId: string) => {
    const res = await apiClient.get<PaginatedResponse<FarmOperation>>(
      `/farm-operations/project/${projectId}`
    );
    return res.data;
  },

  search: async (params: { page?: number; size?: number; search?: string; projectId?: string; type?: string; status?: string } = {}) => {
    const { page = 0, size = 10, search, projectId, type, status } = params;
    const qs = new URLSearchParams({ page: page.toString(), size: size.toString() });
    if (search) qs.set('search', search);
    if (projectId) qs.set('projectId', projectId);
    if (type) qs.set('type', type);
    if (status) qs.set('status', status);
    const res = await apiClient.get<PaginatedResponse<FarmOperation>>(`/farm-operations/search?${qs}`);
    return res.data;
  },

  create: async (data: CreateFarmOperationRequest) => {
    const res = await apiClient.post<FarmOperation>('/farm-operations', data);
    return res.data;
  },

  update: async (id: string, data: CreateFarmOperationRequest) => {
    const res = await apiClient.put<FarmOperation>(`/farm-operations/${id}`, data);
    return res.data;
  },

  updateStatus: async (id: string, status: OperationStatus) => {
    const res = await apiClient.patch<FarmOperation>(
      `/farm-operations/${id}/status?status=${status}`
    );
    return res.data;
  },

  moveToProject: async (id: string, targetProjectId: string) => {
    const res = await apiClient.patch<FarmOperation>(
      `/farm-operations/${id}/move?targetProjectId=${targetProjectId}`
    );
    return res.data;
  },

  // Tasks
  getTasksByOperation: async (farmOperationId: string) => {
    const res = await apiClient.get<OperationTask[]>(
      `/operation-tasks/operation/${farmOperationId}`
    );
    return res.data;
  },

  createTask: async (data: CreateOperationTaskRequest) => {
    const res = await apiClient.post<OperationTask>('/operation-tasks', data);
    return res.data;
  },

  updateTask: async (taskId: string, data: CreateOperationTaskRequest) => {
    const res = await apiClient.put<OperationTask>(`/operation-tasks/${taskId}`, data);
    return res.data;
  },

  completeTask: async (taskId: string, actualCost: number, notes?: string) => {
    const params = new URLSearchParams({ actualCost: actualCost.toString() });
    if (notes) params.append('completionNotes', notes);
    const res = await apiClient.patch<OperationTask>(
      `/operation-tasks/${taskId}/complete?${params}`
    );
    return res.data;
  },

  // Updates only actualCost, leaving schedule/estimate/assignee untouched — unlike updateTask,
  // which is a full-replace PUT and would null/reset those fields if only actualCost is sent.
  updateTaskActualCost: async (taskId: string, actualCost: number) => {
    const params = new URLSearchParams({ actualCost: actualCost.toString() });
    const res = await apiClient.patch<OperationTask>(
      `/operation-tasks/${taskId}/actual-cost?${params}`
    );
    return res.data;
  },

  getScheduledTasksBetween: async (start: string, end: string) => {
    const res = await apiClient.get<OperationTask[]>(
      `/operation-tasks/scheduled?start=${start}&end=${end}`
    );
    return res.data;
  },

  skipTask: async (taskId: string, reason?: string) => {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    const res = await apiClient.patch<OperationTask>(`/operation-tasks/${taskId}/skip${params}`);
    return res.data;
  },

  moveTask: async (taskId: string, targetFarmOperationId: string) => {
    const res = await apiClient.patch<OperationTask>(
      `/operation-tasks/${taskId}/move?targetFarmOperationId=${targetFarmOperationId}`
    );
    return res.data;
  },

  // Harvest records
  getAllHarvestRecords: async () => {
    const res = await apiClient.get<HarvestRecord[]>('/harvest-records');
    return res.data;
  },

  getHarvestRecordsByDateRange: async (start: string, end: string) => {
    const res = await apiClient.get<HarvestRecord[]>(
      `/harvest-records/date-range?start=${start}&end=${end}`
    );
    return res.data;
  },

  getHarvestRecords: async (farmOperationId: string) => {
    const res = await apiClient.get<HarvestRecord[]>(
      `/harvest-records/operation/${farmOperationId}`
    );
    return res.data;
  },

  createHarvestRecord: async (data: CreateHarvestRecordRequest) => {
    const res = await apiClient.post<HarvestRecord>('/harvest-records', data);
    return res.data;
  },

  updateHarvestRecord: async (id: string, data: CreateHarvestRecordRequest) => {
    const res = await apiClient.put<HarvestRecord>(`/harvest-records/${id}`, data);
    return res.data;
  },

  deleteHarvestRecord: async (id: string) => {
    await apiClient.delete(`/harvest-records/${id}`);
  },

  getHarvestRecordById: async (id: string) => {
    const res = await apiClient.get<HarvestRecord>(`/harvest-records/${id}`);
    return res.data;
  },
};

export default farmOperationService;
